// Manual grid tools (P3.12) — FR-13.
//
// **The escape hatch, and the arithmetic behind it.** PRD R-3 names bad beatgrids as the thing that
// destroys trust in Sync, and P3.3 measured roughly one track in twenty coming out wrong. FR-13 is
// the answer: three corrections a human can apply by hand — tap a tempo, nudge the grid, halve or
// double it.
//
// Pure and here for the same reason `sync.ts` and `loop.ts` are: the UI raises these gestures, the
// audio thread consumes their result, and `ui-no-engine-internals` exists so neither drags the other
// into its bundle. None of it is signal processing.
//
// ## An override, not an edit
//
// A correction is stored **beside** the detector's answer, never on top of it. Three reasons, in
// increasing order of how much they cost to get wrong:
//
//  1. **Reversibility.** `clear` restores the detected grid exactly, because it was never
//     overwritten. A correction a user cannot undo is a trap, especially one applied by tapping.
//  2. **Two different facts.** `tempo.ts` already keeps `rawBpm` beside `bpm` so that "the detector
//     found 174 and the octave rule halved it" stays distinguishable from "the detector found 87".
//     "The detector found 174 and a human said 87" is a third fact and deserves the same treatment.
//  3. ⚠️ **Invalidation.** `ANALYSIS_SCHEMA_VERSION` throws away every stored record when a detector
//     improves. A manual correction is *literally a statement that the detector was wrong*, so
//     discarding it when the detector changes is exactly backwards. Overrides live in their own
//     store, like P3.10's cues, and the upgrade never touches them.
//
// ## Seconds, not frames — and this one is free
//
// P3.10's cues are stored in frames and need `cuesFor` to rescale them when the device rate changes.
// A grid is already seconds on both fields (`SyncGrid`), so an override is rate-independent by
// construction and there is no conversion to get wrong. Worth stating only because the neighbouring
// store needed one and a reader may reasonably wonder where this one's went.

import type { SyncGrid } from './sync';
import { isSyncableGrid } from './sync';

/**
 * The widest tempo a manual correction may produce, and the narrowest.
 *
 * Deliberately **wider than the detector's** reporting range (`TEMPO_MIN_BPM` 70 … `TEMPO_MAX_BPM`
 * 190) and narrower than its search range (35 … 330). The detector's range encodes what it can
 * reliably find; this one encodes what a human could plausibly mean. A DJ correcting a half-time
 * reading to 43.5 BPM is doing something unusual but coherent; a tap sequence yielding 900 BPM is a
 * mis-tap, and 900 is not a tempo anybody meant.
 *
 * **Refused rather than clamped.** A clamp silently produces a grid the user did not ask for and did
 * not notice — the failure mode this whole file exists to prevent. Out of range comes back as a
 * refusal with a reason, and the previous grid stands.
 */
export const MANUAL_BPM_MIN = 40;
export const MANUAL_BPM_MAX = 300;

/** How a grid came to be what it is. Carried so the UI can say, and so a bug can be traced to the
 * gesture that caused it rather than to "the grid". */
export type GridEditSource = 'tap' | 'nudge' | 'octave';

/**
 * A human's correction to one track's grid.
 *
 * `beatSeconds` rather than a BPM, and `grid.ts`'s header is emphatic about why: "a consumer that
 * re-derived `60 / bpm` from a rounded BPM would introduce a period error of its own — which is
 * precisely the quantity the 25 ms/minute budget is about". The BPM shown to a human is derived from
 * this, never the other way round.
 */
export interface GridOverride {
  readonly anchorSeconds: number;
  readonly beatSeconds: number;
  readonly source: GridEditSource;
  /** Unix milliseconds. For a human reading the store, and for FF-2's rekordbox import to reason
   * about which of two answers is newer. */
  readonly updatedAt: number;
}

/** BPM from a beat period. The only place this conversion is written for display. */
export function bpmOf(beatSeconds: number): number {
  return 60 / beatSeconds;
}

/** Beat period from a BPM. The inverse, used only where a human supplied the BPM. */
export function beatSecondsOf(bpm: number): number {
  return 60 / bpm;
}

/** Is this tempo one a manual tool is allowed to produce? */
export function isManualBpmInRange(bpm: number): boolean {
  return Number.isFinite(bpm) && bpm >= MANUAL_BPM_MIN && bpm <= MANUAL_BPM_MAX;
}

/**
 * Brings an anchor into `[0, beatSeconds)`.
 *
 * A grid is a *set of times*, so shifting the anchor by a whole beat changes nothing about it —
 * `anchor`, `anchor + beat` and `anchor − 5·beat` all describe the same grid. Normalising keeps the
 * stored number small and readable and, more usefully, keeps a readout stable: without it, nudging
 * a grid earlier past zero would show a large negative anchor and look like a bug.
 *
 * Matches `fitGrid`'s own convention — the first beat at or after the start of the track.
 */
export function normalizeAnchor(anchorSeconds: number, beatSeconds: number): number {
  if (!(beatSeconds > 0) || !Number.isFinite(anchorSeconds)) {
    return anchorSeconds;
  }
  const wrapped = anchorSeconds % beatSeconds;
  return wrapped < 0 ? wrapped + beatSeconds : wrapped;
}

/**
 * The grid a deck should actually use: the human's, if there is one.
 *
 * **The override wins unconditionally**, which is the whole of FR-13 in one line. It is not merged
 * with the detected grid, not averaged against it, and not overruled when the detector later becomes
 * more confident — a person tapped along to the track and the detector did not.
 */
export function effectiveGrid(
  detected: SyncGrid | null,
  override: GridOverride | null,
): SyncGrid | null {
  if (override !== null) {
    const grid = {
      anchorSeconds: override.anchorSeconds,
      beatSeconds: override.beatSeconds,
    };
    // A stored row can be nonsense — hand-edited in devtools, half-written, from a future version.
    // `isSyncableGrid` is the same gate every other consumer applies, so a bad override degrades to
    // "no correction" rather than to a deck that divides by zero.
    return isSyncableGrid(grid) ? grid : detected;
  }
  return detected;
}

/**
 * How much a corrected grid is trusted, for the purposes of everything that asks.
 *
 * **1, and not the detector's number.** `LOOP_QUANTIZE_CONFIDENCE` gates whether loops snap and
 * `TEMPO_SURE_ENOUGH` gates whether the readout hedges — both were reading a figure the detector
 * produced about *its own* answer, which says nothing about a grid a human supplied. Leaving the low
 * confidence in place after a correction would mean the escape hatch fixed the grid and left the app
 * still refusing to use it, which is the one outcome FR-13 must not have.
 */
export function effectiveConfidence(
  detectedConfidence: number,
  override: GridOverride | null,
): number {
  return override === null ? detectedConfidence : 1;
}

// ---------------------------------------------------------------------------
// Tap tempo
// ---------------------------------------------------------------------------

/**
 * How long a gap ends a tap sequence, in seconds.
 *
 * At the detector's slowest reported tempo (70 BPM) a beat is 857 ms, and a user tapping half-time
 * against that is 1.71 s apart. 2.5 s sits clear of both, so a deliberate sequence is never broken
 * up, while a user who taps four times, thinks, and taps again starts fresh rather than averaging a
 * five-second "interval" into their tempo.
 */
export const TAP_TIMEOUT_SECONDS = 2.5;

/**
 * Taps needed before a tempo is reported.
 *
 * Four taps is three intervals. Two taps (one interval) is a tempo with no way to tell a steady hand
 * from a twitch, and every tap tempo worth using has asked for more than that. The fit below gets
 * better with each additional tap and nothing stops the user from continuing.
 */
export const TAP_MINIMUM = 4;

/**
 * ⚠️ **Taps needed before the result is good enough to meet AC-2's drift budget — measured, and it
 * is far more than anyone would guess.**
 *
 * A grid's drift is set by its *period*, and AC-2 allows 25 ms per minute, which at 128 BPM is a
 * tempo error of **0.053 BPM**. Fitting a period from taps is a slope estimate, whose standard error
 * falls as `n^-1.5` — so accuracy improves quickly with tap count, from a very poor start. Measured
 * against this file's own fit, with 25 ms of hand jitter at 128 BPM:
 *
 * | taps | tempo error | drift        |
 * | ---- | ----------- | ------------ |
 * | 4    | 1.58 BPM    | 740 ms/min   |
 * | 8    | 0.54 BPM    | 254 ms/min   |
 * | 16   | 0.30 BPM    | 143 ms/min   |
 * | 32   | 0.04 BPM    | **20 ms/min**|
 *
 * **So four taps is thirty times over budget**, and the honest reading is that tap tempo is a tool
 * for fixing a *grossly* wrong tempo — an octave error, a detector that found nothing — and not for
 * producing a grid that will hold a four-minute blend on its own. Thirty-two taps is eight bars,
 * which is a reasonable thing to ask of someone deliberately correcting a track, and is the point
 * where the tapped grid becomes as good as a detected one.
 *
 * The UI shows the count against this number for exactly that reason: a user who stops at four has
 * a grid that looks corrected and drifts a beat every ten seconds, which is worse than the honest
 * "low confidence" they started with.
 */
export const TAP_TRUSTWORTHY = 32;

/**
 * What a tap run of this length is good for.
 *
 * ⚠️ **The three regimes were measured above and then not acted on, and that was a correctness bug
 * rather than a polish one.** Until this function existed, `tapGrid` applied the fit the moment
 * `fitTaps` returned one — at `TAP_MINIMUM`, four taps — while the readout beside it still said
 * "4 OF 32 — KEEP TAPPING". Found on real music: a 98 BPM four-tap guess went live under a counter
 * asking for twenty-eight more.
 *
 * That is worse than a premature grid, because of what happens downstream. A stored override is an
 * override, and `effectiveConfidence` returns **1** for every one of them — deliberately, so that
 * FR-13's escape hatch is not overruled by the detector it exists to correct. So a four-tap run
 * became a grid trusted *more* than a good detected one: `LOOP_QUANTIZE_CONFIDENCE` snapped loops to
 * it and Sync aligned the other deck to it, at the 740 ms/minute the table above measures — thirty
 * times AC-2's budget. The honest low confidence the user started from was better.
 *
 * - `too-few` — fewer than three intervals. There is no tempo here.
 * - `preview` — a real tempo, shown so the user can see the run converging, and **not applied**.
 *   Nothing downstream may see it.
 * - `applied` — the run has reached the point where a tapped grid is as good as a detected one.
 *
 * A single function so the readout and the store cannot disagree about which regime is which, which
 * is exactly how they came to disagree in the first place.
 */
export type TapStage = 'too-few' | 'preview' | 'applied';

export function tapStage(tapCount: number): TapStage {
  if (tapCount < TAP_MINIMUM) {
    return 'too-few';
  }
  return tapCount < TAP_TRUSTWORTHY ? 'preview' : 'applied';
}

/**
 * One tap, in both clocks.
 *
 * ⚠️ **Both, and that is the trap this file is most careful about.** A user taps along with what
 * they *hear*, and what they hear is the track at the deck's current rate. At +8% on the pitch fader
 * the audible beat is 8% faster than the track's own tempo — so a period taken from wall-clock and
 * stored as the grid would be **8% wrong**, and wrong in a way that looks perfectly plausible in the
 * readout.
 *
 * `trackSeconds` is the playhead, which advances at the deck's rate, so intervals measured in it are
 * already in track time and need no correction. It is only usable while the deck is moving; when it
 * is paused the playhead is frozen and the wall clock is the only one running — and at rest there is
 * no rate to apply, so the wall clock is then correct by the same argument. `fitTaps` picks between
 * them on exactly that basis.
 */
export interface Tap {
  /** `performance.now()` in seconds. Always advances. */
  readonly wallSeconds: number;
  /** The playhead in track seconds at the moment of the tap. Frozen while paused. */
  readonly trackSeconds: number;
}

export type TapFitFailure =
  | 'too-few-taps'
  | 'out-of-range'
  /** The taps were not a tempo — degenerate spacing, a zero interval, or times that went backwards
   * (a seek mid-sequence). Distinct from `out-of-range`, which is a real tempo nobody meant. */
  | 'not-a-tempo';

export interface TapFit {
  readonly grid: SyncGrid | null;
  /** Which clock the period came from. Reported so a test can assert the rate trap is handled and a
   * human can be told that a paused tap sets the tempo but not the downbeat. */
  readonly clock: 'track' | 'wall';
  readonly failure: TapFitFailure | null;
}

/**
 * Least-squares fit of `t_n = anchor + n · period` over a run of taps.
 *
 * **A regression, not a mean of the gaps** — though for the period alone the two agree, since the
 * intervals telescope. What the regression adds is the *anchor*, fitted across every tap at once, so
 * a hand that is 20 ms late on one beat moves the downbeat by 20 ms divided by the number of taps
 * rather than by 20 ms. That is the same argument `fitGrid` makes for taking its anchor from a DFT
 * phase instead of the tallest onset, and it matters for the same reason: AC-2's whole drift budget
 * is 25 ms per minute.
 *
 * Returns null for fewer than two points or a degenerate spread.
 */
function regress(times: readonly number[]): { anchor: number; period: number } | null {
  const n = times.length;
  if (n < 2) {
    return null;
  }
  // Indices are 0…n-1, so their mean is closed-form and the denominator below is a constant — but
  // both are written out rather than substituted, because the day this fits non-consecutive taps
  // (a user skipping a beat) the closed forms silently stop being right.
  let sumIndex = 0;
  let sumTime = 0;
  for (let i = 0; i < n; i += 1) {
    sumIndex += i;
    sumTime += times[i] ?? 0;
  }
  const meanIndex = sumIndex / n;
  const meanTime = sumTime / n;

  let covariance = 0;
  let variance = 0;
  for (let i = 0; i < n; i += 1) {
    const di = i - meanIndex;
    covariance += di * ((times[i] ?? 0) - meanTime);
    variance += di * di;
  }
  if (!(variance > 0)) {
    return null;
  }
  const period = covariance / variance;
  return { anchor: meanTime - period * meanIndex, period };
}

/**
 * Drops every tap before the most recent gap longer than `TAP_TIMEOUT_SECONDS`.
 *
 * Kept separate from the fit so the UI can show "3 taps" while the user is still tapping without
 * duplicating the rule for what counts as the current run.
 */
export function currentTapRun(taps: readonly Tap[]): readonly Tap[] {
  let start = 0;
  for (let i = 1; i < taps.length; i += 1) {
    const previous = taps[i - 1];
    const current = taps[i];
    if (previous === undefined || current === undefined) {
      continue;
    }
    if (current.wallSeconds - previous.wallSeconds > TAP_TIMEOUT_SECONDS) {
      start = i;
    }
  }
  return taps.slice(start);
}

/**
 * Turns a run of taps into a grid.
 *
 * **The period comes from the track clock when the deck is moving and the wall clock when it is
 * not** — see `Tap` for why that is a correctness matter and not a convenience. The anchor is only
 * meaningful in the track clock: a wall-clock anchor names an instant in the user's afternoon, not a
 * position in the track. So a paused tap sets the tempo and **keeps the detected downbeat**, which
 * is why `TapFit.clock` is reported rather than being an internal detail.
 */
export function fitTaps(taps: readonly Tap[], detected: SyncGrid | null): TapFit {
  const run = currentTapRun(taps);
  if (run.length < TAP_MINIMUM) {
    return { grid: null, clock: 'wall', failure: 'too-few-taps' };
  }

  // "Moving" is measured, not asked. The caller knows whether it *told* the deck to play; only the
  // playhead knows whether it actually advanced between the first tap and the last, which is the
  // thing that makes the track clock usable. A deck that ended mid-sequence reads as paused here and
  // falls back correctly rather than fitting a period against a frozen number.
  const first = run[0];
  const last = run[run.length - 1];
  const advanced =
    first !== undefined && last !== undefined && last.trackSeconds > first.trackSeconds;
  const clock: 'track' | 'wall' = advanced ? 'track' : 'wall';

  const times = run.map((tap) => (advanced ? tap.trackSeconds : tap.wallSeconds));
  const fit = regress(times);
  if (fit === null || !(fit.period > 0)) {
    return { grid: null, clock, failure: 'not-a-tempo' };
  }

  const bpm = bpmOf(fit.period);
  if (!isManualBpmInRange(bpm)) {
    return { grid: null, clock, failure: 'out-of-range' };
  }

  // A wall-clock fit has a period worth keeping and an anchor that means nothing here, so the
  // detected downbeat is retained. With no detected grid either, the anchor falls back to zero — the
  // start of the track is as good a first beat as any, and `nudge` is the tool for moving it.
  const anchorSeconds = advanced ? fit.anchor : (detected?.anchorSeconds ?? 0);

  return {
    grid: {
      anchorSeconds: normalizeAnchor(anchorSeconds, fit.period),
      beatSeconds: fit.period,
    },
    clock,
    failure: null,
  };
}

// ---------------------------------------------------------------------------
// Nudge and octave
// ---------------------------------------------------------------------------

/**
 * The two nudge sizes, in milliseconds.
 *
 * Coarse is 10 ms because that is AC-2's own phase-alignment tolerance — one press moves the grid by
 * the whole budget Sync is judged against, which makes it the right unit for "this is visibly off".
 * Fine is 1 ms for settling the last of it, and is finer than the 25 ms/minute drift budget by a
 * comfortable margin, so a user can always reach a grid better than the one they are correcting.
 */
export const GRID_NUDGE_MILLIS = { fine: 1, coarse: 10 } as const;

/**
 * Moves a grid earlier or later without changing its tempo.
 *
 * Sign convention: **positive is later**, matching the anchor it moves. A user pressing "→" expects
 * the beats to move right along the waveform, which is later in the track.
 */
export function nudgeGrid(grid: SyncGrid, millis: number): SyncGrid | null {
  if (!isSyncableGrid(grid) || !Number.isFinite(millis)) {
    return null;
  }
  return {
    anchorSeconds: normalizeAnchor(grid.anchorSeconds + millis / 1000, grid.beatSeconds),
    beatSeconds: grid.beatSeconds,
  };
}

/**
 * Halves or doubles the tempo — FR-13's fix for the octave error P3.3 spends most of its length on.
 *
 * **The anchor is untouched, and that is correct rather than lazy.** Doubling the tempo inserts a
 * beat between every existing pair; halving it removes every other one. In both cases the anchor is
 * still a beat, because the new grid's beats are a superset or a subset of the old one's. Recomputing
 * it would move a downbeat the user had already got right.
 *
 * Returns null when the result leaves the manual range — refused, not clamped, per `MANUAL_BPM_MIN`.
 */
export function scaleGridTempo(grid: SyncGrid, factor: 0.5 | 2): SyncGrid | null {
  if (!isSyncableGrid(grid)) {
    return null;
  }
  const beatSeconds = grid.beatSeconds / factor;
  if (!isManualBpmInRange(bpmOf(beatSeconds))) {
    return null;
  }
  return {
    anchorSeconds: normalizeAnchor(grid.anchorSeconds, beatSeconds),
    beatSeconds,
  };
}

/** Wraps a corrected grid as a storable override. One place stamps the clock. */
export function overrideFrom(
  grid: SyncGrid,
  source: GridEditSource,
  now: number,
): GridOverride {
  return {
    anchorSeconds: grid.anchorSeconds,
    beatSeconds: grid.beatSeconds,
    source,
    updatedAt: now,
  };
}
